import { toLabelValue } from '@/common/convertToDropdownOptions';
import { Form, FormLabel } from '@/components/forms';
import FormCombobox from '@/components/forms/FormCombobox';
import { FormTagInput } from '@/components/forms/FormTagInput';
import Select from '@/components/forms/Select';
import { nameValidationRegex } from '@/constants/regex';
import { Button } from '@/design-system/button';
import { Textarea } from '@/design-system/textarea';
import { useAttributes } from '@/hooks/useAttributes';
import { useDropdown } from '@/hooks/useDropdown';
import { useDynamicLocale } from '@/hoslog-intl';
import { cn } from '@/lib/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { TagsOption } from '../constants/tagsOptions.constant';
import useCreateEditorsPick from '../hooks/useAddEditorsPick';
import useUpdateEditorsPick from '../hooks/useUpdateEditorsPick';
import { EditorPickFormValues, editorPickSchema } from '../utils/editorPick.schema';
import { useTagHandler } from '../utils/useTagHandler';

type EditorPickFormProps =
  | { mode: 'add'; location: string; editorId?: never; defaultValues?: never }
  | { mode: 'update'; editorId: string; defaultValues: EditorPickFormValues; location?: never };

export const EditorPickForm = (props: EditorPickFormProps) => {
  const isUpdate = props.mode === 'update';
  const form = useForm<EditorPickFormValues>({
    resolver: zodResolver(editorPickSchema),
    mode: 'onBlur',
    defaultValues: isUpdate
      ? props.defaultValues
      : {
          location: props.location,
          clubId: '',
          description: '',
          tags: [],
        },
  });

  const {
    formState: { errors },
  } = form;
  const { data: locationOptions = [] } = useAttributes('location');
  const selectedLocation = form.watch('location');
  const { data: clubLocations = [] } = useDropdown(
    'clubLoc',
    selectedLocation,
    undefined,
    isUpdate ? undefined : 'EditorsPick',
  );
  const store = toLabelValue(clubLocations, {
    labelKey: 'label',
    valueKey: 'value',
  });

  const { t } = useDynamicLocale();
  const { mutateAsyncAddEditorsPick } = useCreateEditorsPick();
  const { mutateAsyncUpdateEditorsPick } = useUpdateEditorsPick();
  const handleTag = useTagHandler(form);
  const handleSubmit = async () => {
    const formData = form.getValues();
    const tag = (formData.tags ?? []).map((tag) => tag.text).join(',');
    if (isUpdate) {
      await mutateAsyncUpdateEditorsPick({
        editorPickId: props.editorId,
        description: formData.description,
        tags: tag,
      });
    }
    if (!isUpdate) {
      await mutateAsyncAddEditorsPick({
        locationId: formData.location,
        clubId: formData.clubId,
        description: formData.description,
        tags: tag,
      });
    }
  };
  return (
    <Form form={form} onSubmit={handleSubmit}>
      <div className="w-full space-y-4.5 max-h-[500px] overflow-y-auto px-0.5 pb-1">
        <div className="flex flex-col gap-4.5">
          <Select
            label="common.form.label.editorsPickLocation"
            {...form.register('location')}
            placeholder={t('common.form.placeholder.location')}
            options={locationOptions}
            disabled={isUpdate}
            forceRequired
          />
          <FormCombobox
            {...form.register('clubId')}
            label="common.form.label.storeName"
            name="clubId"
            placeholder={t('common.form.placeholder.storeName')}
            options={store}
            disabled={isUpdate}
            forceRequired
          />
          <div className="flex flex-col gap-2">
            <FormLabel>
              <div className="flex">
                {t('common.form.label.editorsPickDescription')}
                <span className="text-destructive-text leading-[100%]">*</span>
              </div>
            </FormLabel>
            <Textarea
              {...form.register('description')}
              placeholder={t('common.form.placeholder.description')}
              maxLength={200}
              className={cn(
                'h-[64px] disabled:opacity-50 disabled:bg-card placeholder:text-muted-foreground placeholder:font-medium placeholder:text-control leading-[24px] overflow-y-auto px-[13px] py-2',
                errors.description && 'border-destructive focus-visible:ring-destructive/20',
              )}
            />
            {errors.description && (
              <p className="text-sm text-destructive-text font-medium">{t(errors.description.message as string)}</p>
            )}
          </div>
          <div className="flex flex-col gap-2">
            <FormTagInput
              {...form.register('tags')}
              label="common.form.label.tags"
              name="tags"
              placeholder={t('common.form.placeholder.tags')}
              maxTags={4}
              tagRegex={nameValidationRegex}
            />
            <div className="flex gap-2 w-[340px] overflow-x-auto  md:w-[508px]">
              {TagsOption &&
                TagsOption.map((item) => (
                  <Button
                    type="button"
                    key={item.label}
                    onClick={() => handleTag(item)}
                    className="inline-flex items-center rounded-xs font-bold border text-fine px-3 h-6 py-0 border-dashed border-primary bg-card text-primary  leading-[100%] hover:bg-card "
                  >
                    {item.label}
                  </Button>
                ))}
            </div>
          </div>
        </div>
      </div>
    </Form>
  );
};
